/**
 * StoreLocatorBlock — Map-based store/retailer finder for hub pages.
 *
 * Renders a searchable list of store locations with optional browser
 * geolocation for distance sorting. Uses Haversine formula for
 * client-side distance calculation.
 */

import { useState, useMemo, useCallback } from "react";
import { SurfaceStoreLocatorConfigResponse, SurfaceStoreLocationResponse } from "@/hooks/hub/useGetHub";
import "./StoreLocatorBlock.scss";

interface StoreLocatorBlockProps {
    config?: SurfaceStoreLocatorConfigResponse;
    sectionTitle?: string;
}

interface GeoState {
    lat: number;
    lng: number;
    active: boolean;
    loading: boolean;
    error?: string;
}

/**
 * Haversine distance in miles between two lat/lng points.
 * Same approach as backend addressUtils.ts.
 */
function haversineDistanceMiles(
    lat1: number, lng1: number,
    lat2: number, lng2: number,
): number {
    const R = 3958.8; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

function formatAddress(location: SurfaceStoreLocationResponse): string {
    const parts: string[] = [];
    if (location.address) parts.push(location.address);
    if (location.city) parts.push(location.city);
    if (location.state && location.zipCode) {
        parts.push(`${location.state} ${location.zipCode}`);
    } else if (location.state) {
        parts.push(location.state);
    } else if (location.zipCode) {
        parts.push(location.zipCode);
    }
    return parts.join(", ");
}

export const StoreLocatorBlock = ({ config, sectionTitle }: StoreLocatorBlockProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [geoState, setGeoState] = useState<GeoState>({
        lat: 0, lng: 0, active: false, loading: false,
    });

    const locations = config?.locations || [];
    const maxResults = config?.maxResults || 10;
    const searchRadiusMiles = config?.searchRadiusMiles || 25;

    const requestGeolocation = useCallback(() => {
        if (geoState.active) {
            setGeoState({ lat: 0, lng: 0, active: false, loading: false });
            return;
        }

        if (!navigator.geolocation) {
            setGeoState(prev => ({ ...prev, error: "Geolocation not supported" }));
            return;
        }

        setGeoState(prev => ({ ...prev, loading: true, error: undefined }));

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setGeoState({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    active: true,
                    loading: false,
                });
            },
            () => {
                setGeoState({
                    lat: 0, lng: 0,
                    active: false,
                    loading: false,
                    error: "Location access denied",
                });
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
        );
    }, [geoState.active]);

    const filteredLocations = useMemo(() => {
        let results = [...locations];

        // Text search filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            results = results.filter(loc =>
                loc.name.toLowerCase().includes(q) ||
                loc.city && loc.city.toLowerCase().includes(q) ||
                loc.state && loc.state.toLowerCase().includes(q) ||
                loc.zipCode && loc.zipCode.includes(q) ||
                loc.address && loc.address.toLowerCase().includes(q) ||
                loc.category && loc.category.toLowerCase().includes(q));
        }

        // Calculate distances and sort if geolocation is active
        if (geoState.active) {
            type LocWithDist = SurfaceStoreLocationResponse & { _distance?: number };
            const withDistance: LocWithDist[] = results.map(loc => {
                if (loc.lat != null && loc.lng != null) {
                    const dist = haversineDistanceMiles(geoState.lat, geoState.lng, loc.lat, loc.lng);
                    return { ...loc, _distance: dist };
                }
                return { ...loc, _distance: undefined };
            });

            // Filter by radius
            const filtered = withDistance.filter(loc =>
                loc._distance === undefined || loc._distance <= searchRadiusMiles);

            // Sort: locations with distance first (nearest), then without
            filtered.sort((a, b) => {
                if (a._distance != null && b._distance != null) return a._distance - b._distance;
                if (a._distance != null) return -1;
                if (b._distance != null) return 1;
                return 0;
            });

            results = filtered;
        }

        return results.slice(0, maxResults);
    }, [locations, searchQuery, geoState.active, geoState.lat, geoState.lng, maxResults, searchRadiusMiles]);

    if (locations.length === 0) return null;

    const hasGeoLocations = locations.some(l => l.lat != null && l.lng != null);

    return (
        <div className="store-locator">
            {(sectionTitle || config?.heading) &&
                <h3 className="store-locator__title">
                    {sectionTitle || config?.heading}
                </h3>
            }

            {config?.description &&
                <p className="store-locator__description">{config.description}</p>
            }

            <div className="store-locator__controls">
                <input
                    type="text"
                    className="store-locator__search"
                    placeholder="Search by name, city, or zip code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                {hasGeoLocations &&
                    <button
                        type="button"
                        className={`store-locator__geo-btn ${geoState.active ? "store-locator__geo-btn--active" : ""}`}
                        onClick={requestGeolocation}
                        disabled={geoState.loading}
                    >
                        {geoState.loading ? "Locating..." : geoState.active ? "Near me (on)" : "Near me"}
                    </button>
                }
            </div>

            {geoState.error &&
                <p className="store-locator__results-info">{geoState.error}</p>
            }

            <p className="store-locator__results-info">
                {filteredLocations.length === 0
                    ? "No locations found"
                    : `Showing ${filteredLocations.length} location${filteredLocations.length !== 1 ? "s" : ""}`
                }
                {geoState.active && ` within ${searchRadiusMiles} miles`}
            </p>

            {filteredLocations.length === 0
                ? <div className="store-locator__empty">
                    <p>No locations match your search.</p>
                    {searchQuery && <p>Try a different search term or clear the filter.</p>}
                </div>
                : <div className="store-locator__list">
                    {filteredLocations.map((location, index) => {
                        const address = formatAddress(location);
                        const distObj = location as SurfaceStoreLocationResponse & { _distance?: number };
                        const distance = distObj._distance;

                        return (
                            <div key={location._id || `loc-${index}`} className="store-locator__card">
                                <div className="store-locator__card-header">
                                    <h4 className="store-locator__card-name">{location.name}</h4>
                                    {location.category &&
                                        <span className="store-locator__card-category">{location.category}</span>
                                    }
                                </div>

                                {address &&
                                    <p className="store-locator__card-address">{address}</p>
                                }

                                <div className="store-locator__card-details">
                                    {location.phone &&
                                        <span className="store-locator__card-detail">{location.phone}</span>
                                    }
                                    {location.hours &&
                                        <span className="store-locator__card-detail">{location.hours}</span>
                                    }
                                    {distance != null &&
                                        <span className="store-locator__card-distance">
                                            {distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`}
                                        </span>
                                    }
                                </div>

                                {location.url &&
                                    <a
                                        href={location.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="store-locator__card-link"
                                    >
                                        View details &rarr;
                                    </a>
                                }
                            </div>
                        );
                    })}
                </div>
            }
        </div>
    );
};
